import { Stack, useLocalSearchParams } from 'expo-router';
import { useMutation } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import Box from '@/components/UI/Box';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';

const DeleteBadgeAssignees = graphql(/* GraphQL */ `
  mutation DeleteBadgeAssignees($assigneeIds: [uuid!]!) {
    delete_badge_assignee(where: { id: { _in: $assigneeIds } }) {
      affected_rows
    }
  }
`);

type BadgeAssigneeDeleteProps = {
  onDeleted: () => void;
};

export default function BadgeAssigneeDelete({
  onDeleted,
}: BadgeAssigneeDeleteProps) {
  const {
    assigneeId,
    assigneeIds: assigneeIdsParam,
    assignmentCount,
  } = useLocalSearchParams<{
    assigneeId?: string;
    assigneeIds?: string;
    assignmentCount?: string;
  }>();
  const assigneeIds = getBadgeAssigneeIds(assigneeIdsParam ?? assigneeId);
  const count = getAssignmentCount({
    assignmentCount,
    fallbackCount: assigneeIds.length,
  });
  const deleteLabel =
    count === 1 ? 'this badge assignment' : `${count} badge assignments`;
  const deleteButtonLabel =
    count === 1 ? 'Delete Assignment' : 'Delete Assignments';
  const [, deleteBadgeAssignees] = useMutation(DeleteBadgeAssignees);

  const onPressDelete = async () => {
    if (!assigneeIds.length) {
      return { success: false };
    }

    const result = await deleteBadgeAssignees({
      assigneeIds,
    });

    if (result.error || !result.data?.delete_badge_assignee?.affected_rows) {
      return { success: false };
    }
    return { success: true };
  };

  return (
    <Box flex background="surface">
      <Stack.Screen options={{ title: deleteButtonLabel }} />
      <ContentBoundary
        data={assigneeIds.length ? assigneeIds : null}
        empty={<EmptyPlaceholder message="No assignments selected." />}
      >
        {(assigneeIds) => (
          <DestructiveAction
            key={assigneeIds.join(',')}
            label={deleteButtonLabel}
            submittingLabel="Deleting…"
            testID="BadgeAssigneeDeleteConfirmButton"
            onAction={onPressDelete}
            onSuccess={onDeleted}
          >
            <DestructiveActionHeading>
              Are you sure you want to delete {deleteLabel}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This cannot be undone.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </Box>
  );
}

function getBadgeAssigneeIds(value?: string) {
  return (
    value
      ?.split(',')
      .map((assigneeId) => assigneeId.trim())
      .filter((assigneeId) => assigneeId.length > 0) ?? []
  );
}

function getAssignmentCount({
  assignmentCount,
  fallbackCount,
}: {
  assignmentCount?: string;
  fallbackCount: number;
}) {
  const parsedCount = Number.parseInt(assignmentCount ?? '', 10);

  if (!Number.isFinite(parsedCount) || parsedCount < 1) {
    return fallbackCount;
  }

  return parsedCount;
}
